import { X } from "lucide-react"
import { Select, type SelectOption } from "@/components/ui/select"
import { cn } from "@/lib/cn"
import {
  defaultLeaf,
  pickChoices,
  type CustomFieldCatalog,
  type CustomFieldLike,
  type DisplayOp,
  type LeafNode,
  type Scope,
} from "@/lib/predicate-ast"
import { FieldPicker } from "./field-picker"
import { OperatorPicker, OPERATORS_FOR } from "./operator-picker"
import { ValueControl } from "./value-control"

// One predicate leaf row — Scope / Field / Operator / Value / × .
// All controls become read-only when `disabled` is true.

const SCOPE_OPTIONS: SelectOption<Scope>[] = [
  { value: "appuser",      label: "Appuser" },
  { value: "agent_convo",  label: "Conversation" },
]

type Props = {
  leaf: LeafNode
  catalog: CustomFieldCatalog
  onChange: (next: LeafNode) => void
  onRemove: () => void
  disabled?: boolean
}

export function PredicateRow({ leaf, catalog, onChange, onRemove, disabled }: Props) {
  // Resolve the catalog row for the current field — used to render the
  // value control's choice list. For system fields we synthesize.
  const fieldDef: CustomFieldLike & { label: string } | null =
    leaf.fieldType === "system_state"
      ? syntheticSystem(leaf.fieldKey, catalog, leaf.scope)
      : lookupCustom(catalog, leaf.scope, leaf.fieldKey)

  const handleScopeChange = (next: Scope) => {
    // Switching scope clears the field selection — the field key in one
    // scope is meaningless in the other.
    onChange({
      ...leaf,
      scope: next,
      fieldKey: "",
      fieldType: "text",
      value: "",
      warning: undefined,
    })
  }

  const handleFieldChange = (sel: {
    scope: Scope
    fieldKey: string
    fieldDef: CustomFieldLike & { label: string }
    isSystem: boolean
  }) => {
    const fresh = defaultLeaf(sel.scope, {
      key:       sel.fieldKey,
      fieldType: sel.isSystem ? "system_state" : sel.fieldDef.fieldType,
      options:   sel.fieldDef.options,
    })
    onChange({ ...fresh, id: leaf.id })
  }

  const handleOpChange = (next: DisplayOp) => {
    // Reset the value when the op flavor changes (single → array, etc.).
    let value: unknown = leaf.value
    if (next === "contains" && !Array.isArray(value)) value = []
    if (next !== "contains" && Array.isArray(value) && next !== "between") value = ""
    if (next === "between") {
      value = Array.isArray(leaf.value) && leaf.value.length === 2 ? leaf.value : ["", ""]
    }
    if (next === "present" || next === "blank") value = ""
    onChange({ ...leaf, op: next, value, warning: undefined })
  }

  const handleValueChange = (next: unknown) => {
    onChange({ ...leaf, value: next, warning: undefined })
  }

  // If the current op isn't valid for the new field-type (after a field
  // swap), fall back to the first op in the matrix.
  const validOps = OPERATORS_FOR[leaf.fieldType] ?? OPERATORS_FOR.text
  const opIsValid = validOps.some((o) => o.value === leaf.op)
  const effectiveOp: DisplayOp = (opIsValid ? leaf.op : (validOps[0]?.value as DisplayOp)) ?? "eq"

  return (
    <div
      className={cn(
        // 5-col fixed grid is desktop-only — total min-width is ~628 px
        // (Scope 140 + Field 160 + Op 140 + Value 160 + Remove 28 +
        // gaps), so on mobile / narrow side panes the grid overflows.
        // Below `xl:` we stack each control to a single column. This
        // makes a predicate condition take ~5 vertical rows on a phone
        // — verbose but readable, vs. unreadable horizontal scroll.
        "grid items-start gap-2",
        "grid-cols-1 xl:grid-cols-[140px_minmax(160px,1.2fr)_140px_minmax(160px,1.5fr)_28px]",
        leaf.warning && "ring-1 ring-status-warning/40 rounded-control p-1 -m-1",
      )}
    >
      <Select<Scope>
        value={SCOPE_OPTIONS.find((o) => o.value === leaf.scope) ?? null}
        onChange={(o) => o && handleScopeChange(o.value)}
        options={SCOPE_OPTIONS}
        isDisabled={disabled}
        isSearchable={false}
      />

      <FieldPicker
        catalog={catalog}
        scope={leaf.scope}
        fieldKey={leaf.fieldKey}
        isSystem={leaf.fieldType === "system_state"}
        onChange={handleFieldChange}
        disabled={disabled}
      />

      <OperatorPicker
        fieldType={leaf.fieldType}
        value={effectiveOp}
        onChange={handleOpChange}
        disabled={disabled || !leaf.fieldKey}
      />

      <div className="min-w-0">
        <ValueControl
          fieldType={leaf.fieldType}
          fieldDef={fieldDef}
          op={effectiveOp}
          value={leaf.value}
          onChange={handleValueChange}
          disabled={disabled || !leaf.fieldKey}
        />
        {leaf.warning && (
          <div className="mt-1 text-[10px] text-status-warning">
            {warningCopy(leaf.warning)}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className={cn(
          "mt-1 flex h-7 w-7 items-center justify-center rounded-control text-app-muted hover:bg-status-error/10 hover:text-status-error",
          disabled && "opacity-30 hover:bg-transparent hover:text-app-muted",
        )}
        aria-label="Remove condition"
        title="Remove condition"
      >
        <X size={14} />
      </button>
    </div>
  )
}

function lookupCustom(
  catalog: CustomFieldCatalog,
  scope: Scope,
  key: string,
): (CustomFieldLike & { label: string }) | null {
  const list = scope === "appuser" ? catalog.appuser : catalog.agent_convo
  const def = list.find((d) => d.key === key)
  if (!def) return null
  return { key: def.key, label: def.label, fieldType: def.fieldType, options: def.options }
}

function syntheticSystem(
  key: string,
  catalog: CustomFieldCatalog,
  scope: Scope,
): (CustomFieldLike & { label: string }) | null {
  // Most system fields are seeded into the catalog as reserved=true rows;
  // pull the definition through so the value control gets the curated
  // choice list.
  const list = scope === "appuser" ? catalog.appuser : catalog.agent_convo
  const def = list.find((d) => d.key === key && d.reserved)
  if (def) {
    return {
      key:       def.key,
      label:     def.label,
      fieldType: "system_state",
      options:   def.options,
    }
  }
  // Fallback for source_state when the catalog hasn't been seeded yet.
  if (key === "source_state") {
    return {
      key:       "source_state",
      label:     "Source state",
      fieldType: "system_state",
      options:   {},
    }
  }
  return null
}

function warningCopy(w: NonNullable<LeafNode["warning"]>): string {
  switch (w) {
    case "field_archived":             return "This field is archived; the rule won't match anything."
    case "field_missing":              return "Field is no longer in the catalog."
    case "value_no_longer_in_choices": return "Selected value isn't in the field's current choices."
  }
}

// Re-export so the value control can use the same option list type
// without a circular import.
export { pickChoices }
